<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Phone Input Component
      p.text--secondary A specialized input component for entering phone numbers with country code selection and validation.

    // Phone Input Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in phoneInputVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Special handling for v-form validation examples
          template(v-if="variation.id === 'v-form-validation'")
            .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
              .mb-4
                h5.mb-2 Required validation with v-form
                .mb-2
                  small.text--secondary Selected: {{ JSON.stringify(requiredPhoneValue) }}
                v-form(ref="requiredPhoneForm")
                  yz-phone-input(:key="requiredPhoneInputKey" v-model="requiredPhoneValue" required requiredErrorMessage="Phone number is required")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetRequiredPhoneForm")
                    yz-btn(label="Validate" @click="validateRequiredPhoneForm" size="sm" outlined)
                  span.yz-regular-body-xs.ml-2(v-if="requiredPhoneValidationResult" :class="{ 'success--text': requiredPhoneValidationResult === 'Valid', 'error--text': requiredPhoneValidationResult === 'Invalid' }") {{ requiredPhoneValidationResult }}
              
              .mb-4
                h5.mb-2 Business Phone (Optional)
                .mb-2
                  small.text--secondary Selected: {{ JSON.stringify(optionalBusinessPhoneValue) }}
                v-form(ref="optionalBusinessPhoneForm")
                  yz-phone-input(:key="optionalBusinessPhoneInputKey" v-model="optionalBusinessPhoneValue" placeholder="Business phone (optional)")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetOptionalBusinessPhoneForm")
                    yz-btn(label="Validate" @click="validateOptionalBusinessPhoneForm" size="sm" outlined)
                  span.yz-regular-body-xs.ml-2(v-if="optionalBusinessPhoneValidationResult" :class="{ 'success--text': optionalBusinessPhoneValidationResult === 'Valid', 'error--text': optionalBusinessPhoneValidationResult === 'Invalid' }") {{ optionalBusinessPhoneValidationResult }}
              
              .mb-4
                h5.mb-2 Emergency Contact (Optional)
                .mb-2
                  small.text--secondary Selected: {{ JSON.stringify(optionalEmergencyPhoneValue) }}
                v-form(ref="optionalEmergencyPhoneForm")
                  yz-phone-input(:key="optionalEmergencyPhoneInputKey" v-model="optionalEmergencyPhoneValue" placeholder="Emergency contact phone (optional)")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetOptionalEmergencyPhoneForm")
                    yz-btn(label="Validate" @click="validateOptionalEmergencyPhoneForm" size="sm" outlined)
                  span.yz-regular-body-xs.ml-2(v-if="optionalEmergencyPhoneValidationResult" :class="{ 'success--text': optionalEmergencyPhoneValidationResult === 'Valid', 'error--text': optionalEmergencyPhoneValidationResult === 'Invalid' }") {{ optionalEmergencyPhoneValidationResult }}

          // Grouped variations (side by side)
          template(v-else-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
              .d-flex.flex-column.gap-4
                div(v-for="phoneInput in variation.phoneInputs" :key="phoneInput.label" class="d-flex flex-column align-start mb-4")
                  small.text--secondary.mb-2(v-if="phoneInput.hint") {{ phoneInput.hint }}
                  small.font-weight-medium.mb-2 {{ phoneInput.label }}
                  yz-phone-input(v-model="phoneInput.value" v-bind="phoneInput.props")
                  small.text--secondary.mt-1(v-if="phoneInput.value") Value: {{ phoneInput.value }}

          // Special handling for v-form validation code examples  
          template(v-if="variation.id === 'v-form-validation'")
            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === requiredPhoneCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === requiredPhoneCode ? 'success' : ''" @click="copyCode(requiredPhoneCode)")
                    span.font-weight-medium.ml-2 Required phone validation
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ requiredPhoneCode }}
                
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === optionalBusinessPhoneCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalBusinessPhoneCode ? 'success' : ''" @click="copyCode(optionalBusinessPhoneCode)")
                    span.font-weight-medium.ml-2 Optional business phone
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ optionalBusinessPhoneCode }}
                
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === optionalEmergencyPhoneCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalEmergencyPhoneCode ? 'success' : ''" @click="copyCode(optionalEmergencyPhoneCode)")
                    span.font-weight-medium.ml-2 Optional emergency contact
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ optionalEmergencyPhoneCode }}

          // Regular code examples for grouped variations
          template(v-else-if="variation.isGroup")
              .variation-code
                .code-header.d-flex.align-center.mb-2
                  span.font-weight-medium Code Examples

                .code-examples
                  div(v-for="phoneInput in variation.phoneInputs" :key="phoneInput.label" class="mb-3")
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === phoneInput.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === phoneInput.code ? 'success' : ''" @click="copyCode(phoneInput.code)")
                      span.font-weight-medium.ml-2 {{ phoneInput.label }}
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ phoneInput.code }}

          // Individual variations
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
              small.font-weight-medium.mb-2 {{ variation.label }}
              yz-phone-input(v-model="variation.value" v-bind="variation.props")
              small.text--secondary.mt-2(v-if="variation.value") Value: {{ variation.value }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // Methods Documentation
    .component-section.mb-8#methods
      h3.mb-4.cursor-pointer(@click="navigateToSection('methods')") Methods
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Method
                th Returns
                th Description
            tbody
              tr(v-for="method in phoneInputMethods" :key="method.name")
                td.font-weight-medium.text-no-wrap {{ method.name }}
                td.text--secondary.text-no-wrap {{ method.returns }}
                td.single-line {{ method.description }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in phoneInputProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in phoneInputEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      // V-Form Validation Data
      requiredPhoneInputKey: 0,
      optionalBusinessPhoneInputKey: 0,
      optionalEmergencyPhoneInputKey: 0,
      requiredPhoneValue: null,
      optionalBusinessPhoneValue: null,
      optionalEmergencyPhoneValue: null,
      requiredPhoneValidationResult: null,
      optionalBusinessPhoneValidationResult: null,
      optionalEmergencyPhoneValidationResult: null,
      phoneInputVariations: [
        {
          name: 'Basic Phone Input',
          id: 'basic-phone-input',
          description: 'Default phone input with country selection',
          label: 'Basic Phone Input',
          value: '',
          props: {},
          code: 'yz-phone-input(v-model="phoneNumber")'
        },
        {
          name: 'Phone Input with Placeholder',
          id: 'placeholder-phone-input',
          description: 'Phone input with custom placeholder',
          label: 'Custom Placeholder',
          value: '',
          props: {
            placeholder: 'Enter your mobile number'
          },
          code: 'yz-phone-input(v-model="phoneNumber" placeholder="Enter your mobile number")'
        },
        {
          name: 'Component Sizes',
          id: 'phone-input-sizes',
          description: 'Different sizes of the phone input component',
          isGroup: true,
          phoneInputs: [
            {
              label: 'Small Size',
              value: '',
              props: {
                size: 'sm'
              },
              code: 'yz-phone-input(v-model="phoneNumber" size="sm")',
              hint: 'Small size input'
            },
            {
              label: 'Medium Size',
              value: '',
              props: {
                size: 'md'
              },
              code: 'yz-phone-input(v-model="phoneNumber" size="md")',
              hint: 'Medium size input (default)'
            },
            {
              label: 'Large Size',
              value: '',
              props: {
                size: 'lg'
              },
              code: 'yz-phone-input(v-model="phoneNumber" size="lg")',
              hint: 'Large size input'
            }
          ]
        },
        {
          name: 'States',
          id: 'phone-input-states',
          description: 'Different states of the phone input component',
          isGroup: true,
          phoneInputs: [
            {
              label: 'Default State',
              value: '',
              props: {},
              code: 'yz-phone-input(v-model="phoneNumber")',
              hint: 'Normal state'
            },
            {
              label: 'Loading State',
              value: '',
              props: {
                loading: true
              },
              code: 'yz-phone-input(v-model="phoneNumber" loading)',
              hint: 'Shows loading indicator'
            },
            {
              label: 'Disabled State',
              value: '60123456789',
              props: {
                disabled: true
              },
              code: 'yz-phone-input(v-model="phoneNumber" disabled)',
              hint: 'Disabled input'
            }
          ]
        },
        {
          name: 'Background Color Options',
          id: 'background-color-options',
          description: 'Phone input with different background colors',
          isGroup: true,
          phoneInputs: [
            {
              label: 'Default Background',
              value: '',
              props: {},
              code: 'yz-phone-input(v-model="phoneNumber")',
              hint: 'Default background color'
            },
            {
              label: 'White Background',
              value: '',
              props: {
                backgroundColor: 'white'
              },
              code: 'yz-phone-input(v-model="phoneNumber" background-color="white")',
              hint: 'White background'
            },
            {
              label: 'Hex Color Background',
              value: '',
              props: {
                backgroundColor: '#f8f9fa'
              },
              code: 'yz-phone-input(v-model="phoneNumber" background-color="#f8f9fa")',
              hint: 'Using hex color format (#f8f9fa)'
            },
            {
              label: 'YZ Design Token',
              value: '',
              props: {
                backgroundColor: 'yz-blue-100'
              },
              code: 'yz-phone-input(v-model="phoneNumber" background-color="yz-blue-100")',
              hint: 'Using YZ design token (yz-blue-100)'
            },
            {
              label: 'CSS Variable',
              value: '',
              props: {
                backgroundColor: 'var(--yz-green-100)'
              },
              code: 'yz-phone-input(v-model="phoneNumber" background-color="var(--yz-green-100)")',
              hint: 'Using CSS variable format'
            },
            {
              label: 'Short Token',
              value: '',
              props: {
                backgroundColor: 'neutral-200'
              },
              code: 'yz-phone-input(v-model="phoneNumber" background-color="neutral-200")',
              hint: 'Using short design token (neutral-200)'
            }
          ]
        },
        {
          name: 'V-Form Validation',
          id: 'v-form-validation',
          description: 'Phone input integration with v-form validation system - demonstrating required and optional validation patterns',
          isGroup: true,
          phoneInputs: []
        }
      ],
      phoneInputProps: [
        {
          name: 'value (v-model)',
          type: 'String',
          default: 'null',
          description: 'The phone number value including country code (e.g., "60123456789")'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the field is required. Shows validation errors when enabled.'
        },
        {
          name: 'placeholder',
          type: 'String',
          default: 'null',
          description: 'Placeholder text for the phone number input field'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows a loading progress bar below the input'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables both country selector and phone number input'
        },
        {
          name: 'backgroundColor',
          type: 'String',
          default: 'white',
          description: 'Sets the background color of both the country selector and phone number input. Supports multiple formats: hex (#ffffff), CSS variables (var(--yz-color)), YZ design tokens (yz-neutral-100), and short tokens (neutral-100)'
        },
        {
          name: 'size',
          type: 'String',
          default: 'lg',
          description: 'Size of the phone input component. Options: "sm" (small), "md" (medium), "lg" (large)'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: 'This field is required',
          description: 'Custom error message to display when required validation fails'
        }
      ],
      phoneInputMethods: [
        {
          name: 'validate(force)',
          returns: 'Boolean',
          description: 'Validates the current phone number input and returns true if valid, false otherwise. Pass force=true to show validation errors immediately. Integrates with v-form validation.'
        },
        {
          name: 'resetValidation()',
          returns: 'void',
          description: 'Clears validation errors without resetting the phone number value. Useful for resetting form validation state.'
        }
      ],
      phoneInputEvents: [
        {
          name: 'input',
          parameters: 'phoneNumber: String',
          description: 'Emitted when the phone number changes. Returns full number with country code.'
        },
        {
          name: 'change',
          parameters: 'none',
          description: 'Emitted when the phone number value changes (no parameters)'
        },
        {
          name: 'focus',
          parameters: 'focused: Boolean',
          description: 'Emitted when input gains focus. Parameter indicates focus state (false when focused).'
        },
        {
          name: 'blur',
          parameters: 'blurred: Boolean',
          description: 'Emitted when input loses focus. Parameter indicates blur state (true when blurred).'
        }
      ]
    }
  },
  computed: {
    // Optional Validation Rules for v-form examples
    optionalBusinessPhoneRules() {
      return [
        v => !v || (v && v.length >= 10) || 'Business phone must be at least 10 digits if provided'
      ]
    },
    optionalEmergencyPhoneRules() {
      return [
        v => !v || (v && v.length >= 10) || 'Emergency contact phone must be at least 10 digits if provided'
      ]
    },
    
    // Code examples for v-form validation
    requiredPhoneCode() {
      return `v-form(ref="phoneForm")
  yz-phone-input(v-model="phoneNumber" required requiredErrorMessage="Phone number is required")

// Validation methods
methods: {
  validateForm() {
    if (this.$refs.phoneForm.validate()) {
      console.log('Form is valid!')
    }
  },
  resetForm() {
    this.$refs.phoneForm.resetValidation()
    this.phoneNumber = null
  }
}`
    },
    optionalBusinessPhoneCode() {
      return `v-form(ref="businessPhoneForm")
  yz-phone-input(v-model="businessPhone" placeholder="Business phone (optional)")
`
    },
    optionalEmergencyPhoneCode() {
      return `v-form(ref="emergencyPhoneForm")
  yz-phone-input(v-model="emergencyPhone" placeholder="Emergency contact phone (optional)")
`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    // V-Form Validation Methods
    validateRequiredPhoneForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.requiredPhoneForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.requiredPhoneValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.requiredPhoneValidationResult = null;
          }, 1500);
        }
      });
    },
    resetRequiredPhoneForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.requiredPhoneForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.requiredPhoneValidationResult = null;
          this.requiredPhoneValue = null;
          this.requiredPhoneInputKey++;
        }
      });
    },
    validateOptionalBusinessPhoneForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalBusinessPhoneForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalBusinessPhoneValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalBusinessPhoneValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalBusinessPhoneForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalBusinessPhoneForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalBusinessPhoneValidationResult = null;
          this.optionalBusinessPhoneValue = null;
          this.optionalBusinessPhoneInputKey++;
        }
      });
    },
    validateOptionalEmergencyPhoneForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalEmergencyPhoneForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalEmergencyPhoneValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalEmergencyPhoneValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalEmergencyPhoneForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalEmergencyPhoneForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalEmergencyPhoneValidationResult = null;
          this.optionalEmergencyPhoneValue = null;
          this.optionalEmergencyPhoneInputKey++;
        }
      });
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>